import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Paintbrush,
  Users,
  Folder,
  Bell,
  Lock,
  Settings,
  ShieldCheck,
} from "lucide-react";
/**
 * LandingPage.jsx — CHI Laminate Artflow
 * Rebranded around CHI Limited's identity (red + blue), with
 * "Powered by 1879 Tech Hub" as a supporting credit.
 *
 * Drop-in replacement. Single file. No external CSS or UI libraries.
 * Keeps the same public API (default export) as the previous version.
 */
export default function LandingPage() {
  const [activeModal, setActiveModal] = useState(null);

  const footerModalContent = {
    privacy: {
      title: "Privacy Policy",
      body: "CHI Laminate Artflow collects only the information needed to manage artwork approvals across CHI Limited teams and vendors. Personal data is handled securely and is never shared outside CHI without consent.",
    },
    terms: {
      title: "Terms of Service",
      body: "CHI Laminate Artflow is provided by CHI Limited for internal artwork review, approval, and vendor collaboration. Access and actions are governed by CHI Limited company policies and assigned user permissions.",
    },
    security: {
      title: "Security",
      body: "The platform uses encrypted transport, role-based access controls, and audit logging to protect CHI artwork and account data. Regular reviews and monitoring keep the service safe for CHI admins and vendors.",
    },
  };

  const openModal = (type) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  return (
    <div className="chi-root">
      <style>{css}</style>

      {activeModal && (
        <div className="chi-modal-overlay">
          <div className="chi-modal">
            <div className="chi-modal-header">
              <h3 className="chi-modal-title">
                {footerModalContent[activeModal].title}
              </h3>
              <button
                className="chi-modal-close"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="chi-modal-body">
              <p>{footerModalContent[activeModal].body}</p>
            </div>
          </div>
        </div>
      )}

      {/* NAV */}
      <header className="chi-nav">
        <div className="chi-nav-inner">
          <a href="/" className="chi-brand" aria-label="CHI Laminate Artflow">
            <img
              src="/chivita.png"
              alt="Chivita logo"
              className="chi-brand-mark"
              style={{ height: 42, width: "auto", objectFit: "contain" }}
            />
            <span className="chi-brand-text">
              <span className="chi-brand-text-top">CHI</span>
              <span className="chi-brand-text-bottom">Laminate Artflow</span>
            </span>
          </a>
          <nav className="chi-nav-links" aria-label="Primary">
            <a href="#features">Features</a>
            <a href="#workflow">Workflow</a>
            <a href="#roles">Who it's for</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="chi-nav-cta">
            <Link to="/admin/login" className="chi-btn chi-btn-primary">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="chi-hero">
        <div className="chi-hero-glow" aria-hidden="true" />
        <div className="chi-container chi-hero-grid">
          <div>
            <span className="chi-eyebrow">
              <ShieldCheck size={14} strokeWidth={2.5} />
              A CHI Limited product
            </span>
            <h1 className="chi-h1">
              Artwork approval, the <em>CHI</em> way.
            </h1>
            <p className="chi-lede">
              CHI Laminate Artflow is the single hub where CHI teams and vendors
              submit, review, and ship laminate and print artwork through every
              approval stage — with automated notifications, role controls, and
              a clean audit trail.
            </p>

            <div className="chi-hero-actions">
              <Link to="/admin/login" className="chi-btn chi-btn-primary chi-btn-lg">
                Sign in
              </Link>
              <a href="#workflow" className="chi-btn chi-btn-outline chi-btn-lg">
                See how it works
              </a>
            </div>

            <div className="chi-hero-meta">
              <p>✓ Role-based access</p>
              <p>✓ SSO ready</p>
              <p>✓ Automated follow-ups</p>
            </div>
          </div>

          {/* Mock preview card */}
          <div className="chi-hero-preview">
            <div className="chi-window">
              <div className="chi-window-bar">
                <span />
                <span />
                <span />
                <span className="chi-window-title">
                </span>
              </div>
              <div className="chi-window-body">
                <div className="chi-preview-art">
                  <div className="chi-preview-card">
                    <div className="chi-preview-badge">Live workflow</div>
                    <h3>Packaging approval in motion</h3>
                    <p>Approvals, comments, and handoffs are tracked in one place.</p>
                    <div className="chi-preview-metrics">
                      <div>
                        <strong>4</strong>
                        <span>Stages</span>
                      </div>
                      <div>
                        <strong>24/7</strong>
                        <span>Alerts</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="chi-preview-side">
                  <div className="chi-panel-header">
                    <span className="chi-panel-kicker">Current status</span>
                    <span className="chi-panel-pill">In review</span>
                  </div>
                  <div className="chi-stage">
                    <span className="chi-stage-dot chi-done" />
                    <span>Stage 1 · NAFDAC review</span>
                  </div>
                  <div className="chi-stage">
                    <span className="chi-stage-dot chi-done" />
                    <span>Stage 2 · Pre-vendor</span>
                  </div>
                  <div className="chi-stage">
                    <span className="chi-stage-dot chi-active" />
                    <span>Stage 3 · Position proof</span>
                  </div>
                  <div className="chi-stage chi-muted">
                    <span className="chi-stage-dot" />
                    <span>Stage 4 · Colour proof</span>
                  </div>
                  <Link
                    to="/admin/login"
                    className="chi-btn chi-btn-primary chi-btn-block"
                  >
                    Review artwork
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="chi-section chi-section-alt">
        <div className="chi-container">
          <div className="chi-section-head">
            <span className="chi-tag">Features</span>
            <h2 className="chi-h2">
              Everything CHI's approval pipeline needs
            </h2>
            <p className="chi-sub">
              Purpose-built for CHI Limited's laminate and print artwork — from
              first upload to final sign-off.
            </p>
          </div>

          <div className="chi-features">
            {features.map((f) => (
              <article key={f.title} className="chi-feature">
                <div className="chi-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="chi-section">
        <div className="chi-container">
          <div className="chi-section-head">
            <span className="chi-tag chi-tag-blue">Workflow</span>
            <h2 className="chi-h2">
              A clear stage, every step of the way
            </h2>
            <p className="chi-sub">
              The CHI Laminate Artflow pipeline moves through four structured
              approval stages, from initial packaging review to final colour
              proof sign-off.
            </p>
          </div>

          <ol className="chi-steps">
            {steps.map((s, i) => (
              <li key={s.title} className="chi-step">
                <span className="chi-step-num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h4>{s.title}</h4>
                  <p>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ROLES */}
      <section id="roles" className="chi-section chi-section-alt">
        <div className="chi-container chi-roles">
          <article className="chi-role-card">
            <span className="chi-tag">For CHI admins</span>
            <h3>Review, approve, and ship with confidence</h3>
            <ul>
              <li>SSO-ready access for internal CHI approvers</li>
              <li>Comment, reject, or approve in one click</li>
              <li>Full audit trail on every asset</li>
              <li>
                Coordinate approvals across NAFDAC, vendor handoff, position
                proof, and colour proof stages
              </li>
            </ul>
          </article>
          <article className="chi-role-card chi-role-card-dark">
            <span className="chi-tag chi-tag-light">For CHI vendors</span>
            <h3>Submit artwork and track it in real time</h3>
            <ul>
              <li>Simple onboarding with password-based sign-in</li>
              <li>Upload images and PDFs with auto-validation</li>
              <li>Email + in-app status notifications</li>
              <li>Resubmit revisions without losing history</li>
            </ul>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="chi-cta">
        <div className="chi-container chi-cta-inner">
          <h2 className="chi-h2">
            Ready to shorten CHI's approval cycle?
          </h2>
          <p>
            Get your CHI team onto CHI Laminate Artflow in under a day. Migrate
            existing vendors, set roles, and start approving.
          </p>
          <div className="chi-hero-actions">
            <Link to="/admin/login" className="chi-btn chi-btn-primary chi-btn-lg">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="chi-footer">
        <div className="chi-container chi-footer-grid">
          <div>
            <div className="chi-brand" aria-label="CHI Laminate Artflow">
              <img
                src="/chivita.png"
                alt="Chivita logo"
                className="chi-brand-mark"
                style={{ height: 42, width: "auto", objectFit: "contain" }}
              />
              <span className="chi-brand-text">
                <span className="chi-brand-text-top">CHI</span>
                <span className="chi-brand-text-bottom">Laminate Artflow</span>
              </span>
            </div>
            <p className="chi-footer-note">
              CHI Limited's approval workflow for laminate &amp; print artwork.
            </p>
          </div>

          <div>
            <h5>Product</h5>
            <a href="#features">Features</a>
            <a href="#workflow">Workflow</a>
            <a href="#roles">Roles</a>
          </div>

          <div>
            <h5>Legal</h5>
            <button
              type="button"
              className="chi-link-button"
              onClick={() => openModal("privacy")}
            >
              Privacy
            </button>
            <button
              type="button"
              className="chi-link-button"
              onClick={() => openModal("terms")}
            >
              Terms
            </button>
            <button
              type="button"
              className="chi-link-button"
              onClick={() => openModal("security")}
            >
              Security
            </button>
          </div>

          <div>
            <h5>Powered by</h5>
            <div className="chi-powered">
              <img src="/1879-22.png" alt="1879 logo" style={{ height: 34, width: "auto", objectFit: "contain" }} />
              <span>1879 Tech Hub</span>
            </div>
          </div>
        </div>

        <div className="chi-container chi-footer-bottom">
          <span>
            © {new Date().getFullYear()} CHI Limited. CHI Laminate Artflow. All
            rights reserved.
          </span>
          <span className="chi-footer-credit">
            Powered by 1879 Tech Hub
          </span>
        </div>
      </footer>
    </div>
  );
}

const features = [
  {
    title: "Artwork management",
    body: "Create, edit, review, and track CHI artwork through every processing stage without losing context.",
    icon: <Paintbrush size={20} />,
  },
  {
    title: "Vendor onboarding",
    body: "Invite CHI vendors, verify accounts, and manage their submissions from a single dashboard.",
    icon: <Users size={20} />,
  },
  {
    title: "File uploads",
    body: "Accept images and PDFs with size, format, and resolution checks handled automatically.",
    icon: <Folder size={20} />,
  },
  {
    title: "Notifications & email",
    body: "Automated alerts, follow-ups, and digest emails keep CHI teams aligned — no chasing.",
    icon: <Bell size={20} />,
  },
  {
    title: "Auth & SSO",
    body: "Regular login and enterprise SSO with role-based access control out of the box.",
    icon: <Lock size={20} />,
  },
  {
    title: "Automation scripts",
    body: "Background jobs advance stages, clean up notifications, and seed admins on schedule.",
    icon: <Settings size={20} />,
  },
];

const steps = [
  {
    title: "Stage 1 — Packaging design review",
    body: "Internal CHI teams review the artwork and checklist before NAFDAC approval.",
  },
  {
    title: "Stage 2 — Pre-vendor approval",
    body: "The design is reviewed again and approved before it is released to the vendor.",
  },
  {
    title: "Stage 3 — Position proof approval",
    body: "Vendors upload the position proof and the CHI team approves or requests changes.",
  },
  {
    title: "Stage 4 — Colour proof / chromalin",
    body: "The final colour proof is reviewed and acknowledged before production.",
  },
];

const css = `
:root {
  --chi-red: #e30613;
  --chi-red-2: #b8000f;
  --chi-red-3: #8a0009;
  --chi-blue: #0033a0;
  --chi-blue-2: #001f6b;
  --chi-blue-3: #4a6fd4;
  --chi-bg: #f7f6f2;
  --chi-bg-2: #eef1f8;
  --chi-surface: #ffffff;
  --chi-surface-2: #f4f6fb;
  --chi-border: rgba(0, 31, 107, 0.10);
  --chi-text: #0e1730;
  --chi-muted: #5b6478;
  --chi-radius: 14px;
  --chi-shadow: 0 20px 60px -20px rgba(0, 31, 107, 0.20);
  --chi-font: 'Sen', Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.chi-root {
  font-family: var(--chi-font);
  color: var(--chi-text);
  background: var(--chi-bg);
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}
.chi-root *, .chi-root *::before, .chi-root *::after { box-sizing: border-box; }
.chi-root a { color: inherit; text-decoration: none; }
.chi-root .chi-link-button {
  background: none;
  border: 0;
  padding: 4px 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
}
.chi-root .chi-link-button + .chi-link-button {
  margin-top: 6px;
}
.chi-root .chi-link-button:hover {
  color: var(--chi-red);
}
.chi-root h1, .chi-root h2, .chi-root h3, .chi-root h4, .chi-root h5, .chi-root p, .chi-root ul, .chi-root ol { margin: 0; }
.chi-root ul, .chi-root ol { padding: 0; list-style: none; }

.chi-container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* NAV */
.chi-nav {
  position: sticky; top: 0; z-index: 40;
  background: rgba(255,255,255,0.92);
  backdrop-filter: saturate(180%) blur(16px);
  border-bottom: 1px solid var(--chi-border);
}
.chi-nav-inner {
  max-width: 1160px; margin: 0 auto; padding: 14px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
}

/* BRAND — CHI mark + wordmark */
.chi-brand { display: inline-flex; align-items: center; gap: 12px; font-weight: 800; letter-spacing: -0.01em; }
.chi-brand-mark {
  width: auto;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: transparent;
  color: inherit;
  box-shadow: none;
  padding: 0;
}
.chi-brand-mark-inner {
  font-family: var(--chi-font);
  font-weight: 800;
  font-size: 18px;
  letter-spacing: 0.02em;
  line-height: 1;
}
.chi-brand-text { display: inline-flex; flex-direction: column; line-height: 1.05; }
.chi-brand-text-top { font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--chi-red); font-weight: 800; }
.chi-brand-text-bottom { font-size: 15px; color: var(--chi-blue-2); font-weight: 800; }

.chi-nav-links { display: flex; gap: 24px; }
.chi-nav-links a { color: var(--chi-text); font-size: 14px; opacity: 0.75; }
.chi-nav-links a:hover { opacity: 1; color: var(--chi-red); }
.chi-nav-cta { display: flex; gap: 10px; }

/* BUTTONS */
.chi-btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 10px 16px; font-size: 14px; font-weight: 700;
  border-radius: 10px; border: 1px solid transparent;
  cursor: pointer; transition: transform .15s ease, background .2s ease, border-color .2s ease, color .2s ease, box-shadow .2s ease;
}
.chi-btn:hover { transform: translateY(-1px); }
.chi-btn-lg { padding: 12px 20px; font-size: 14px; border-radius: 12px; }
.chi-btn-block { width: 100%; margin-top: 14px; }
.chi-btn-primary {
  background: linear-gradient(180deg, var(--chi-red), var(--chi-red-2));
  color: #ffffff !important;
  box-shadow: 0 10px 28px -12px rgba(227, 6, 19, 0.55);
}
.chi-btn-primary:hover { background: linear-gradient(180deg, #ef1a26, var(--chi-red-2)); box-shadow: 0 14px 32px -12px rgba(227, 6, 19, 0.6); }
.chi-btn-outline { background: transparent; border-color: rgba(0, 31, 107, 0.18); color: var(--chi-blue-2); }
.chi-btn-outline:hover { border-color: var(--chi-blue); color: var(--chi-blue); }

/* HERO */
.chi-hero { position: relative; padding: 84px 0 60px; overflow: hidden; }
.chi-hero-glow {
  position: absolute; inset: -20% -10% auto -10%; height: 520px; pointer-events: none;
  background:
    radial-gradient(600px 320px at 20% 20%, rgba(227, 6, 19, 0.18), transparent 55%),
    radial-gradient(600px 320px at 80% 10%, rgba(0, 51, 160, 0.18), transparent 55%);
  filter: blur(10px);
}
.chi-hero-grid { position: relative; display: grid; grid-template-columns: 1.05fr 1fr; gap: 56px; align-items: center; }
.chi-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--chi-red);
  padding: 6px 12px; border: 1px solid rgba(227, 6, 19, 0.22); border-radius: 999px;
  background: rgba(227, 6, 19, 0.08);
}
.chi-h1 { margin-top: 18px; font-size: clamp(36px, 5vw, 60px); line-height: 1.05; letter-spacing: -0.02em; font-weight: 800; color: var(--chi-blue-2); }
.chi-h1 em { font-style: normal; color: var(--chi-red); }
.chi-lede { margin-top: 18px; color: var(--chi-muted); font-size: 18px; max-width: 560px; }
.chi-hero-actions { margin-top: 26px; display: flex; gap: 12px; flex-wrap: wrap; }
.chi-hero-meta { margin-top: 22px; display: flex; gap: 18px; flex-wrap: wrap; color: var(--chi-muted); font-size: 14px; }

/* HERO PREVIEW */
.chi-hero-preview { position: relative; }
.chi-window {
  background: linear-gradient(180deg, var(--chi-surface-2), var(--chi-surface));
  border: 1px solid var(--chi-border);
  border-radius: 16px; box-shadow: var(--chi-shadow);
  overflow: hidden;
}
.chi-window-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 12px 14px; border-bottom: 1px solid var(--chi-border);
  background: linear-gradient(90deg, rgba(0,51,160,0.06), rgba(227,6,19,0.05));
}
.chi-window-bar > span { width: 10px; height: 10px; border-radius: 50%; background: #94a3b8; }
.chi-window-bar > span:nth-child(1) { background: var(--chi-red); }
.chi-window-bar > span:nth-child(2) { background: #f5b800; }
.chi-window-bar > span:nth-child(3) { background: var(--chi-blue); }
.chi-window-title { margin-left: 10px; font-size: 12px; color: var(--chi-muted); font-weight: 600; }
.chi-window-body { display: grid; grid-template-columns: 1.4fr 1fr; gap: 0; }
.chi-preview-art {
  min-height: 300px;
  padding: 20px;
  background:
    radial-gradient(circle at top left, rgba(255,255,255,0.95), transparent 32%),
    linear-gradient(135deg, rgba(227,6,19,0.16), rgba(0,51,160,0.18) 60%, rgba(0,31,107,0.24));
  display: flex;
  align-items: flex-end;
}
.chi-preview-card {
  width: 100%;
  background: rgba(255,255,255,0.9);
  border: 1px solid rgba(255,255,255,0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 20px 36px -24px rgba(0, 31, 107, 0.4);
}
.chi-preview-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--chi-red);
  background: rgba(227, 6, 19, 0.10);
  padding: 6px 10px;
  border-radius: 999px;
}
.chi-preview-card h3 {
  margin-top: 10px;
  font-size: 20px;
  font-weight: 800;
  color: var(--chi-blue-2);
}
.chi-preview-card p {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--chi-muted);
}
.chi-preview-metrics {
  margin-top: 14px;
  display: flex;
  gap: 12px;
}
.chi-preview-metrics > div {
  flex: 1;
  background: rgba(0, 51, 160, 0.06);
  border-radius: 12px;
  padding: 10px 12px;
}
.chi-preview-metrics strong {
  display: block;
  font-size: 16px;
  color: var(--chi-blue-2);
}
.chi-preview-metrics span {
  font-size: 12px;
  color: var(--chi-muted);
}
.chi-preview-side {
  padding: 20px;
  border-left: 1px solid var(--chi-border);
  background: linear-gradient(180deg, rgba(255,255,255,0.8), rgba(248,250,252,0.9));
}
.chi-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.chi-panel-kicker {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--chi-blue);
}
.chi-panel-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(0, 51, 160, 0.10);
  color: var(--chi-blue-2);
}
.chi-stage { display: flex; align-items: center; gap: 10px; padding: 9px 0; font-size: 14px; color: var(--chi-blue-2); }
.chi-stage.chi-muted { color: var(--chi-muted); }
.chi-stage-dot { width: 10px; height: 10px; border-radius: 50%; background: #d1d5db; } 
.chi-stage-dot.chi-done { background: var(--chi-blue-3); }
.chi-stage-dot.chi-active { background: var(--chi-red); box-shadow: 0 0 0 4px rgba(227, 6, 19, 0.18); }

/* SECTION */
.chi-section { padding: 90px 0; }
.chi-section-alt { background: var(--chi-bg-2); border-top: 1px solid var(--chi-border); border-bottom: 1px solid var(--chi-border); }
.chi-section-head { max-width: 720px; margin: 0 auto 46px; text-align: center; }
.chi-h2 { font-size: clamp(28px, 3.6vw, 40px); font-weight: 800; letter-spacing: -0.02em; margin-top: 12px; color: var(--chi-blue-2); }
.chi-sub { color: var(--chi-muted); margin-top: 12px; font-size: 17px; }

/* TAGS */
.chi-tag {
  display: inline-block; font-size: 12px; font-weight: 800; letter-spacing: 0.12em;
  text-transform: uppercase; padding: 6px 12px; border-radius: 999px;
  background: rgba(227, 6, 19, 0.10); color: var(--chi-red);
  border: 1px solid rgba(227, 6, 19, 0.24);
}
.chi-tag-blue { background: rgba(0, 51, 160, 0.10); color: var(--chi-blue); border-color: rgba(0, 51, 160, 0.22); }
.chi-tag-light { background: rgba(255,255,255,0.9); color: var(--chi-blue-2); border-color: rgba(255,255,255,0.8); }

/* FEATURES */
.chi-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.chi-feature {
  background: var(--chi-surface); border: 1px solid var(--chi-border);
  border-radius: var(--chi-radius); padding: 24px;
  transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
}
.chi-feature:hover { transform: translateY(-3px); border-color: rgba(227,6,19,0.24); box-shadow: 0 20px 40px -24px rgba(0,31,107,0.25); }
.chi-feature-icon {
  width: 44px; height: 44px; display: grid; place-items: center;
  border-radius: 10px;
  color: var(--chi-red);
  background: linear-gradient(135deg, rgba(227,6,19,0.10), rgba(0,51,160,0.10));
  border: 1px solid var(--chi-border);
}
.chi-feature h3 { margin-top: 14px; font-size: 17px; font-weight: 800; color: var(--chi-blue-2); }
.chi-feature p { margin-top: 8px; color: var(--chi-muted); font-size: 14.5px; }

/* STEPS */
.chi-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.chi-step {
  background: var(--chi-surface); border: 1px solid var(--chi-border);
  border-radius: var(--chi-radius); padding: 22px;
  border-top: 3px solid var(--chi-red);
}
.chi-step h4 { margin-top: 8px; font-size: 15px; font-weight: 800; color: var(--chi-blue-2); }
.chi-step p { color: var(--chi-muted); font-size: 13.5px; margin-top: 6px; }
.chi-step-num { font-size: 12px; letter-spacing: 0.14em; color: var(--chi-red); font-weight: 800; }

/* ROLES */
.chi-roles { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.chi-role-card {
  background: var(--chi-surface); border: 1px solid var(--chi-border);
  border-radius: var(--chi-radius); padding: 30px;
}
.chi-role-card h3 { margin-top: 14px; font-size: 22px; font-weight: 800; letter-spacing: -0.01em; color: var(--chi-blue-2); }
.chi-role-card ul { margin-top: 16px; display: grid; gap: 10px; }
.chi-role-card li { color: var(--chi-muted); padding-left: 22px; position: relative; }
.chi-role-card li::before {
  content: "✓"; position: absolute; left: 0; top: 0; color: var(--chi-red); font-weight: 800;
}
.chi-role-card-dark {
  background: linear-gradient(160deg, var(--chi-blue-2) 0%, var(--chi-blue) 60%, #002a86 100%);
  border-color: rgba(255,255,255,0.08);
  color: #eaf0ff;
}
.chi-role-card-dark h3 { color: #ffffff; }
.chi-role-card-dark li { color: rgba(234, 240, 255, 0.85); }
.chi-role-card-dark li::before { color: #ffd23f; }

/* CTA */
.chi-cta { padding: 90px 0; position: relative; overflow: hidden; }
.chi-cta::before {
  content: ""; position: absolute; inset: 0;
  background:
    radial-gradient(700px 300px at 30% 0%, rgba(227, 6, 19, 0.16), transparent 70%),
    radial-gradient(700px 300px at 70% 0%, rgba(0, 51, 160, 0.16), transparent 70%);
  pointer-events: none;
}
.chi-cta-inner { text-align: center; max-width: 720px; position: relative; }
.chi-cta-inner p { color: var(--chi-muted); margin-top: 12px; font-size: 17px; }
.chi-cta-inner .chi-hero-actions { justify-content: center; }

/* FOOTER */
.chi-footer { border-top: 1px solid var(--chi-border); background: var(--chi-surface); padding: 50px 0 24px; }
.chi-footer-grid { display: grid; grid-template-columns: 1.6fr repeat(3, 1fr); gap: 24px; }
.chi-footer h5 { font-size: 13px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--chi-blue); margin-bottom: 12px; font-weight: 800; }
.chi-footer a { display: block; color: var(--chi-text); font-size: 14px; padding: 4px 0; opacity: 0.85; }
.chi-footer a:hover { opacity: 1; color: var(--chi-red); }
.chi-footer-note { color: var(--chi-muted); margin-top: 12px; font-size: 14px; max-width: 320px; }
.chi-powered {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 8px 12px; border: 1px solid var(--chi-border); border-radius: 10px;
  background: var(--chi-surface-2);
}
.chi-powered img { width: 26px; height: 26px; object-fit: contain; }
.chi-powered span { font-size: 13px; font-weight: 700; color: var(--chi-blue-2); }
.chi-footer-bottom {
  margin-top: 30px; padding-top: 20px; border-top: 1px solid var(--chi-border);
  color: var(--chi-muted); font-size: 13px;
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px;
}
.chi-footer-credit { color: var(--chi-blue); font-weight: 700; }

/* MODAL */
.chi-modal-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(14, 23, 48, 0.65);
  display: grid; place-items: center;
  padding: 20px;
}
.chi-modal {
  width: min(680px, 100%);
  background: var(--chi-surface);
  border-radius: 24px;
  box-shadow: 0 32px 90px rgba(0, 31, 107, 0.30);
  max-height: 90vh; overflow: hidden;
  border-top: 4px solid var(--chi-red);
}
.chi-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; padding: 24px 24px 0;
}
.chi-modal-title { font-size: 22px; font-weight: 800; margin: 0; color: var(--chi-blue-2); }
.chi-modal-close {
  border: none; background: transparent; color: var(--chi-muted);
  font-size: 28px; line-height: 1; cursor: pointer; padding: 6px;
}
.chi-modal-close:hover { color: var(--chi-red); }
.chi-modal-body { padding: 0 24px 24px; color: var(--chi-text); line-height: 1.75; font-size: 15px; }

/* RESPONSIVE */
@media (max-width: 960px) {
  .chi-hero-grid { grid-template-columns: 1fr; }
  .chi-features { grid-template-columns: repeat(2, 1fr); }
  .chi-steps { grid-template-columns: repeat(2, 1fr); }
  .chi-roles { grid-template-columns: 1fr; }
  .chi-footer-grid { grid-template-columns: 1fr 1fr; }
  .chi-nav-links { display: none; }
}
@media (max-width: 560px) {
  .chi-hero { padding: 56px 0 40px; }
  .chi-section { padding: 64px 0; }
  .chi-features { grid-template-columns: 1fr; }
  .chi-steps { grid-template-columns: 1fr; }
  .chi-footer-grid { grid-template-columns: 1fr; }
  .chi-brand-text-bottom { font-size: 13px; }
}
`;
